import React from "react";
import { motion, useInView } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { staggerContainerVariants, cardVariants } from "@/lib/animations";

export interface StaggerContainerProps {
  children: React.ReactNode;
  className?: string;
  /** Viewport threshold to trigger animation (0-1) */
  threshold?: number;
  /** Whether to trigger animation only once */
  once?: boolean;
  /** Delay between each child animation in seconds */
  staggerDelay?: number;
  /** Initial delay before first child animates */
  delayChildren?: number;
}

/**
 * StaggerContainer - 交错动画容器组件
 *
 * 子元素依次延迟出现，创建流畅的入场效果
 * 需要配合 StaggerItem 使用
 *
 * @example
 * ```tsx
 * <StaggerContainer>
 *   <StaggerItem><Card>Item 1</Card></StaggerItem>
 *   <StaggerItem><Card>Item 2</Card></StaggerItem>
 *   <StaggerItem><Card>Item 3</Card></StaggerItem>
 * </StaggerContainer>
 * ```
 */
export function StaggerContainer({
  children,
  className,
  threshold = 0.2,
  once = true,
  staggerDelay = 0.1,
  delayChildren = 0.1,
}: StaggerContainerProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once,
    amount: threshold,
  });
  const prefersReducedMotion = useReducedMotion();

  // 无障碍降级：直接渲染无动画
  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const customVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren,
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={customVariants}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export interface StaggerItemProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * StaggerItem - 交错动画子元素
 *
 * 必须作为 StaggerContainer 的子元素使用
 */
export function StaggerItem({ children, className }: StaggerItemProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div variants={cardVariants} className={className}>
      {children}
    </motion.div>
  );
}

export default StaggerContainer;
